import React from 'react';
import { Box, Typography, Alert } from '@mui/material';
import { getNumericLevel, getDisplayName } from '../../../../../../../constants/skillLevels';
import { REVIEW_TYPES } from '../../../../../../../constants/reviewTypes';

const DiscrepancyAnalysis = ({ 
  skillId, 
  segmentId = null, 
  competencies, 
  skillLevels,
  skillName = '',
  assignment,
  reviewer
}) => {

  // Early return if required data is not available
  if (!skillLevels || !competencies || !skillId || !assignment) {
    return null;
  }

  // Extract reviewers from assignment
  const reviewers = assignment.reviewers || [];
  
  if (!reviewers.length) {
    return null;
  }

  // Only show for sequential reviews
  const reviewType = reviewers[0]?.reviewType;
  if (reviewType !== REVIEW_TYPES.SEQUENTIAL) {
    return null;
  }

  // Only show when the current reviewer order is the lastReviewerOrder
  const currentReviewer = reviewer;
  
  if (!currentReviewer || currentReviewer.order !== assignment.lastReviewerOrder) {
    return null;
  }

  const reviewerLevels = reviewers
    .filter(reviewer => reviewer.order !== assignment.lastReviewerOrder)
    .map(reviewer => {
      const competencyLevelId = segmentId 
        ? competencies.reviewers[reviewer.id]?.skills[skillId]?.segments?.[segmentId]?.competencyLevelId
        : competencies.reviewers[reviewer.id]?.skills[skillId]?.overall;
      
      if (!competencyLevelId) return null;
      
      const result = {
        reviewerId: reviewer.id,
        reviewer,
        competencyLevelId,
        numericLevel: getNumericLevel(competencyLevelId, skillLevels),
        displayName: getDisplayName(competencyLevelId, skillLevels)
      };
      
      return result;
    })
    .filter(Boolean);

  // Need at least 2 reviewers to analyze discrepancy
  if (reviewerLevels.length < 2) {
    return null;
  }

  // Calculate min and max levels
  const numericLevels = reviewerLevels.map(r => r.numericLevel);
  const minLevel = Math.min(...numericLevels);
  const maxLevel = Math.max(...numericLevels);
  const levelDifference = maxLevel - minLevel;

  // Determine discrepancy type and action
  const getDiscrepancyAnalysis = () => {
    // Critical Competence Disagreement - No Competence (level 0) vs Any Competence (level >= 1)
    const hasNoCompetence = numericLevels.includes(0);
    const hasAnyCompetence = numericLevels.some(level => level >= 1);
    
    if (hasNoCompetence && hasAnyCompetence) {
      return {
        type: 'Critical Competence Disagreement',
        description: 'No Competence vs Any Competence - Indicates fundamental misunderstanding or potential risk.',
        action: 'Immediate review',
        severity: 'error',
        color: '#d32f2f'
      };
    }

    // High-Level Agreement - Both evaluators select high-level competence (Specialist 4+, Expert 5+, Master 6+)
    const highLevels = numericLevels.filter(level => level >= 4);
    
    if (levelDifference === 0 && highLevels.length === numericLevels.length && minLevel >= 4) {
      return {
        type: 'High-Level Agreement (Strength Confirmation)',
        description: 'All evaluators select the same high-level competence (Specialist, Expert, or Master).',
        action: 'Recognize and leverage strengths strategically',
        severity: 'success',
        color: '#2e7d32'
      };
    }

    // Severe Discrepancy - ≥ 3 levels difference
    if (levelDifference >= 3) {
      return {
        type: 'Severe Discrepancy',
        description: `3+ levels difference (${levelDifference} levels) - Significant disagreement requiring urgent managerial review.`,
        action: 'Immediate review',
        severity: 'error',
        color: '#d32f2f'
      };
    }

    // Moderate Discrepancy - Exactly 2 levels difference
    if (levelDifference === 2) {
      return {
        type: 'Moderate Discrepancy',
        description: 'Exactly 2 levels difference - Noticeable difference in perception; manager should assess for context or clarification.',
        action: 'Investigate and clarify context',
        severity: 'warning',
        color: '#ed6c02'
      };
    }

    // Mild Discrepancy - Exactly 1 level difference
    if (levelDifference === 1) {
      return {
        type: 'Mild Discrepancy',
        description: 'Exactly 1 level difference - Minor variance; manager discretion for reconciliation.',
        action: 'Optional clarification',
        severity: 'info',
        color: '#0288d1'
      };
    }

    // Perfect Agreement - No difference
    return {
      type: 'Perfect Agreement',
      description: 'All evaluators selected the same competency level.',
      action: 'No action required',
      severity: 'success',
      color: '#2e7d32'
    };
  };

  const analysis = getDiscrepancyAnalysis();

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
        Discrepancy Analysis for {skillName}
      </Typography>
      
      <Alert 
        severity={analysis.severity}
        sx={{ 
          mb: 2,
          '& .MuiAlert-message': {
            width: '100%'
          }
        }}
      >
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            {analysis.type}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {analysis.description}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Action: {analysis.action}
          </Typography>
        </Box>
      </Alert>

    </Box>
  );
};

export default DiscrepancyAnalysis; 